import * as actions_assets from 'actions/assets-actions';
import * as actions_profiles from 'actions/profiles-actions';
import * as actions_service_requests from 'actions/service-requests-actions';
import * as toolbox from 'components/common/toolbox';
import Authorize from 'components/common/authorize';
import ExpandDetails from 'components/common/expand-details';
import React, { useState, useEffect, useRef } from 'react';
import SpeedDialButton from 'components/common/speed-dial-button';
import EllipsisDropdown from 'components/common/ellipsis-dropdown';
import ActivityLog from 'components/activity-log/activity-log';
import _ from 'lodash';
import { APPS, ADDRESSES, CUSTOMERS, LOG, PERMISSIONS } from 'components/constants';
import { Avatar, CloseX, DashboardDetail, ModalForm, Spinner } from 'enspire-manager-framework';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

export default function CustomerDashboard(props) {
	
	/* Hooks -------------------------------------------------------------------------------------------------------------------*/
	
	const seg = 3;
	const params = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const expandRef = useRef();    
	const routeLocation = useLocation();
	const hasPageRendered = useRef(false);
	
	const admin = useSelector((store) => store.admin);
	const assets = useSelector((store) => store.assets);
	const features = useSelector((store) => store.settings?.settings?.features)?.[(params.appId == APPS.DISPATCH.id) ? 'dispatch' : 'assets'];
	const inventory = useSelector((store) => store.inventory);
	const profiles = useSelector((store) => store.profiles);
	const services = useSelector((store) => store.serviceRequests);
	const settings = useSelector((store) => store.settings.settings);
	const user_permissions = useSelector(store => store.users?.user_permissions);
	
	const [state, setState] = useState({ mapAddress: null });
	const [showActivityModal, setShowActivityModal] = useState(false);

	const profile = profiles.profile;
	const pathname = routeLocation.pathname;
	const segments = routeLocation.pathname.split('/').length;
	const segmentsClosed = (segments > 8);

	/* Effects -----------------------------------------------------------------------------------------------------------------*/

	useEffect(() => {
		if (hasPageRendered.current) {
			let location = toolbox.modifyPath(routeLocation?.pathname, seg, null, 7);
			history.replace({ pathname: location });				
		}
		hasPageRendered.current = true;
    }, [params.appId]);

	// Subscribe to Profile to complete address and contact information
	useEffect(() => {
		if (parseInt(params.customer_id)) dispatch(actions_profiles.subProfile(params.handle, params.appId, params.customer_id, 'PROFILE'));
		return () => {
			let unsubscribe = profiles.profile_unsubscribe;
			if (typeof unsubscribe === "function") unsubscribe();
			dispatch(actions_profiles.clearProfile());
		};
		
	}, [params.customer_id, profiles.contact_save_pending]);
		
	// Subscribe to Asset for this Customer
	useEffect(() => {
		if (params.appId == APPS.ASSETS.id) {
			dispatch(actions_assets.subAssets(params.handle, params.customer_id, settings?.features?.assets?.customer_assets));

			return () => {
				const unsubscribe = assets.assets_unsubscribe;
				if (typeof unsubscribe == 'function') unsubscribe();
			}
		}
	}, [params.customer_id, settings?.features?.assets?.customer_assets, params.appId]);

    useEffect(() => {
        dispatch(actions_service_requests.subServiceRequests(params.handle, params.customer_id));

        return () => {
            let unsubscribe = services.service_requests_unsubscribe;
            if (typeof unsubscribe === 'function') unsubscribe();
        };
    }, [params.customer_id, params.appId]);

	useEffect(() => {
		let address = _.find(profile?.addresses, (o) => { return (
			o.addressTypeId == ADDRESSES.DELIVERY.id || 
			o.addressTypeId == ADDRESSES.PHYSICAL.id || 
			o.addressTypeId == ADDRESSES.MAILING.id
		)});
        let mapAddress = (address) ? `${address.addressLine1} ${address.addressLine2} ${address.city} ${address.state} ${address.zip}` : null;
		setState({ ...state, mapAddress });
	}, [profile]);

	useEffect(() => {
		(segmentsClosed) ? expandRef.current?.setClosed() : expandRef.current?.setOpen();
	}, [segmentsClosed])
	
	/* Handlers/Actions --------------------------------------------------------------------------------------------------------*/

	const handleSpeedDial = (value, action) => {
		if (value == 'service_request') handleNewServiceRequest();
		if (value == 'quote') handleNewQuote();
	}
	const handleNewServiceRequest = () => {
		let newLocation = toolbox.modifyPath(routeLocation.pathname, seg+2, 'request-form/0');
		history.push({ pathname: newLocation });
	};
	const handleNewQuote = () => {
		let newLocation = toolbox.modifyPath(routeLocation.pathname, seg+2, 'quote-form/0');
		history.push({ pathname: newLocation });
	};
	const openForm = () => {
		let location = toolbox.modifyPath(pathname, seg+2, `customer-form/${params.customer_id}`);
		history.push({ pathname: location });
	};

	/* Constants ---------------------------------------------------------------------------------------------------------------*/

	var contactArray = []; // start with all contacts, remove when assigned an address
	if (profile?.contacts) {
		profile.contacts.forEach((contact) => {
			contactArray.push(contact);
		});
	}

	const customerType = _.find(Object.values(CUSTOMERS), { id: profile?.customerTypeId });

	/* Render ------------------------------------------------------------------------------------------------------------------*/

	return (
		<div className="row">
			{profile && parseInt(params.customer_id) ? (
				<>
					{ !window.flutter && segments <= seg+5 &&
						<CloseX onClick={() => { history.goBack(); }} className="mr-2 mt-n1" color={ '#aaaaaa' } />
					}
					<div id="customer-avatar" className={ 'col-12 pt-2 dashboard-avatar flex-wrap' }>
						<div className="profile-image">
							<Avatar
								className="mx-auto"
								color="white"
								bgColor="saddleBrown"
								size={95}
								name={profile.displayName}
								// image={}
								border="4px solid white"
							/>
						</div>
						<div className="profile-info d-flex justify-content-center flex-column align-items-start mr-1" style={{ height: '90px' }}>
							<h1 className="m-0">{profile.displayName}</h1>
							<div className="d-flex flex-wrap align-items-start">
								<div>
									{ profile.companyName && <h4>{profile.companyName}</h4> }
									<p className="mt-0 mb-2">{profile.addresses?.[0]?.addressLine1}</p>
								</div>
							</div>
							<span className={ 'badge badge-' + customerType?.badge }>{customerType?.name}</span>
						</div>
						{ !!profile.depositsTotal &&
							<div className="alert alert-info ml-2 mt-2 d-flex align-items-center p-2">
								<div className="text-right" style={{ lineHeight: '1.1', fontSize: '11px' }}>Deposit<br/>Invoiced:</div>
								<span className="h5 m-0 ml-2">{ toolbox.formatNumber(profile.depositsTotal, 2, true) }</span>
							</div>
						}
						{ !!profile.balanceDue &&
							<div className="alert alert-warning ml-2 mt-2 mr-3 d-flex align-items-center p-2">
								<div className="text-right" style={{ lineHeight: '1.1', fontSize: '11px' }}>Balance<br/>Due:</div>
								<span className="h5 m-0 ml-2">{ toolbox.formatNumber(profile.balanceDue, 2, true) }</span>
							</div>
						}
					</div>

					{/* EXPANDABLE DETAILS ------------------------------------------------------ */}

					<div className="col-12">
						<div id="details" className="mb-2">
							<ExpandDetails default={(segmentsClosed) ? 'closed' : 'open'} top={ -110 } noControls={ !segmentsClosed } noEllipsis ref={expandRef}>
								<div className="d-flex align-items-center position-relative">
									<button type="button" 
										className={'btn btn-default btn-sm mr-2' }
										disabled={!user_permissions[PERMISSIONS.CUSTOMERS_EDIT.key]}
										onClick={openForm}><i className="fa-regular fa-gear fa-lg text-muted mr-1"></i>Edit Customer
									</button>
									<EllipsisDropdown actions={[
										...(user_permissions[PERMISSIONS.ACTIVITY_LOGS_VIEW.key]) ? [{ label: 'Activity Log', onClick: () => setShowActivityModal(true) }] : [],
									]} />
								</div>
								<div id="service-request-details" className="mt-3 mb-2">
									<DashboardDetail field={ 'Phone' } value={ profile.phone } />
									<DashboardDetail field={ 'Notes' } value={ profile.notes } />
								</div>
							</ExpandDetails>

							{ profile.deleted &&
								<div className="alert alert-danger mt-3 mb-0 d-flex align-items-center">
									<i className="fa-solid fa-circle-xmark fa-2x mr-3"></i>
									<span>This Customer has been <strong>ARCHIVED!</strong> &nbsp; You may be able to restore it from the <strong>Archive</strong> page on the main menu</span>
								</div>
							}
						</div>
					</div>

					<Authorize appId={ APPS.DISPATCH.id } permKeys={[PERMISSIONS.QUOTES_CREATE.key, PERMISSIONS.SERVICE_REQUESTS_CREATE.key]} and={!admin.bottom_sheet_show && params.form == 'form' && segments <= 8}>
						<SpeedDialButton 
							columnRef={ props.columnRef }
							onClick={handleSpeedDial}
							actions={[
								...(features['generate_quotes'] && user_permissions[PERMISSIONS.QUOTES_CREATE.key]) ? [{
									name: '+ Quote', 
									value: 'quote', 
									className: '', 
									icon: <i className="fa-solid fa-comment-plus fa-xl"></i>
								}] : [],
								...(user_permissions[PERMISSIONS.SERVICE_REQUESTS_CREATE.key]) ? [{
									name: '+ Service Request', 
									value: 'service_request', 
									className: '', 
									icon: <i className="fa-solid fa-folder-plus fa-xl"></i>
								}] : [],
							]}
						/>
					</Authorize>

					{/* ModalForm for Activity Log */}
					{showActivityModal && (
						<ModalForm
							modal_header="Customer Activity"
							cancel_button_title="Close"
							cancel_button_callback={() => setShowActivityModal(false)}
							hide_submit={ true }
							no_padding={ true }
							size="md"
						>
							<ActivityLog 
								id="customer_id" 
								prefix="PROFILE" 
								singular="profile"
								store="profiles" 
								type={ `${LOG.CUSTOMER.key}|${LOG.ADDRESS.key}|${LOG.CONTACT.key}` } 
							/>
						</ModalForm>
					)}
				</>
			) : profiles.vendor_selected_pending ? (
				<Spinner center />
			) : (
				<div className="col-12">
					<h2 className="text-center mb-4">Customer Unavailable</h2>
				</div>
			)}
		</div>
	);
}
